import {FC, useEffect, useState} from 'react';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {ICatalogSEO} from 'modules/products/types';
import {formatNumber, getPluralProducts} from 'core/helpers';
import {useSanitizeString} from 'core/hooks/useSanitizeString';
import styles from './style.module.scss';
import {Box} from 'components/Grid';
import {useIsTabletByScreen} from 'core/hooks/useIsTabletByScreen';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import {getMediaSrc} from 'core/helpers/media';

interface IProps {
    seo?: ICatalogSEO | null;
    total: number;
}

const SCREEN_RESOLUTIONS = {
    DESKTOP: 'DESKTOP',
    TABLET: 'TABLET',
    MOBILE: 'MOBILE',
};

export const InfoSeoTitle: FC<IProps> = ({seo, total}) => {
    const {pageTitle, catalogSeoTextUpper, catalogSeoTextUpperFull, imageForDesktop} = seo || {};
    const upperText = useSanitizeString(catalogSeoTextUpper);
    const isMobile = useIsMobileByScreen();
    const isTablet = useIsTabletByScreen();
    const [view, setView] = useState(SCREEN_RESOLUTIONS.DESKTOP);

    useEffect(() => {
        if (isMobile) {
            setView(SCREEN_RESOLUTIONS.MOBILE);
        } else if (isTablet) {
            setView(SCREEN_RESOLUTIONS.TABLET);
        } else if (!isMobile && !isTablet) {
            setView(SCREEN_RESOLUTIONS.DESKTOP);
        }
    }, [isMobile, isTablet]);

    const upperTextTrimmed = upperText.split('[[TRIM]]')[0];
    const backgroundImage = imageForDesktop ? `url(${getMediaSrc(imageForDesktop, {width: 9999, isWebp: true})})` : '';

    const bannerBaseStyles = {
        backgroundImage,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        borderRadius: '24px',
        marginTop: '4px',
    };

    const desktopBannerStyles = {
        ...bannerBaseStyles,
        minHeight: '174px',
        paddingLeft: '32px',
        paddingTop: upperTextTrimmed ? '32px' : '0',
        paddingBottom: upperTextTrimmed ? '24px' : '0',
        justifyContent: upperTextTrimmed ? 'flex-start' : 'center',
    };

    const tabletBannerStyles = {
        ...bannerBaseStyles,
        height: '132px',
        paddingLeft: '20px',
        paddingTop: '0',
        justifyContent: 'center',
    };

    const renderMobileView = () => (
        <div className={styles.mobile}>
            <Typography data-skeleton-item={true} variant={'ch-title-24-reg'} element={'h1'}>
                {pageTitle}
            </Typography>
            <Typography data-skeleton-item={true} color={'gray60'} variant={'label-14-r-fluid'}>
                {formatNumber(total)} {getPluralProducts(total)}
            </Typography>
        </div>
    );

    const renderDesktopView = () =>
        imageForDesktop ? (
            <div data-skeleton-item={true} className={classNames(styles.desktop)} style={desktopBannerStyles}>
                <Typography
                    data-skeleton-item={true}
                    className={styles.title}
                    variant={'ch-display-42-reg'}
                    element={'h1'}
                >
                    {pageTitle}
                </Typography>
                {upperTextTrimmed && (
                    <div
                        data-skeleton-item={true}
                        className={styles.infoContent}
                        dangerouslySetInnerHTML={{['__html']: upperTextTrimmed}}
                    />
                )}
            </div>
        ) : (
            <Typography
                data-skeleton-item={true}
                className={classNames(styles.desktop)}
                variant={'ch-display-42-reg'}
                element={'h1'}
            >
                {pageTitle}
            </Typography>
        );

    const renderTabletView = () => (
        <>
            {imageForDesktop && (
                <div data-skeleton-item={true} className={classNames(styles.tablet)} style={tabletBannerStyles}>
                    <Typography
                        data-skeleton-item={true}
                        className={styles.title}
                        variant={'headline-32-reg-ch'}
                        element={'h1'}
                    >
                        {pageTitle}
                    </Typography>
                </div>
            )}
            {!imageForDesktop && (
                <div className={classNames(styles.singleTitleTablet)}>
                    <Typography
                        data-skeleton-item={true}
                        className={classNames(styles.tablet)}
                        variant={'headline-32-reg-ch'}
                        element={'h1'}
                    >
                        {pageTitle}
                    </Typography>
                </div>
            )}
            <div className={styles.tablet}>
                <div
                    data-skeleton-item={true}
                    className={styles.infoContentTablet}
                    dangerouslySetInnerHTML={{['__html']: catalogSeoTextUpperFull || ''}}
                />
            </div>
        </>
    );

    return (
        <Box className={styles.main}>
            {view === SCREEN_RESOLUTIONS.MOBILE && renderMobileView()}
            {view === SCREEN_RESOLUTIONS.DESKTOP && renderDesktopView()}
            {view === SCREEN_RESOLUTIONS.TABLET && renderTabletView()}
        </Box>
    );
};
